import {useAuth} from '@saltus/auth';
import React, {ReactNode, useEffect, useMemo, useState} from 'react';
import {Navigate} from 'react-router-dom';

import * as Sentry from '@sentry/react';
import {DjangoContext} from '../../contexts/useAuthContext';
import Loading from '../Loading/Loading';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import useDjango from '../../hooks/useDjango';
import {useMFAError} from '../../hooks/useMFAError';

interface Identities {
  providerName?: string;
  providerType?: string;
}

interface EnforceMfaProps {
  children: ReactNode;
}

// Accounts exempt from MFA enrolment (admin + regression pack test users).
const MFA_EXEMPT_EMAILS = [
  'oneview-admin@saltus.co.uk',
  'oneview-test+regression-admin@saltus.co.uk',
  'oneview-test+regression-sfp@saltus.co.uk',
  'oneview-test+regression-spp-da@saltus.co.uk',
];

function EnforceMfa({children}: EnforceMfaProps) {
  const {getPreferredMFA, user} = useAuth();
  const {djangoUser, notifications} = useDjango();
  const [mfa, setMfa] = useState<string | null>(null);
  const [isAzureAD, setIsAzureAD] = useState(false);
  const {mfaError} = useMFAError();

  useEffect(() => {
    if (!user) return;

    setIsAzureAD(() => {
      const identities = user.attributes?.identities;
      if (identities) {
        const identity = (JSON.parse(identities) as Identities[]).find(
          (item) => item?.providerType === 'SAML' && item?.providerName?.endsWith('AD')
        );
        return !!identity;
      }

      return user.groups?.some((group) => group.endsWith('AD')) ?? false;
    });

    if (!mfa || mfa === 'NOMFA') {
      getPreferredMFA().then(setMfa).catch(mfaError);
    }
  }, [user]);

  const perms: GlobalPermissionEnum[] = useMemo(() => {
    return djangoUser?.perms?.map((x) => x!.codename!) || [];
  }, [djangoUser?.perms]);

  const value = useMemo(() => {
    if (!djangoUser) return undefined;
    const businessUnit = djangoUser.businessUnit!;
    return {
      businessUnit,
      djangoUser,
      perms,
      notifications: notifications || [],
    };
  }, [djangoUser, perms, notifications]);

  useEffect(() => {
    try {
      if (djangoUser?.email) {
        Sentry.setUser({
          id: String(djangoUser.curoId),
          email: djangoUser.email || '',
          username: djangoUser.name || '',
        });
        Sentry.setTag('businessUnit', djangoUser.businessUnit?.name || '');
        Sentry.setTag('partnershipType', djangoUser.businessUnit?.partnershipType || '');
        Sentry.setTag(
          'permissions',
          djangoUser.perms?.map((perm) => perm?.codename).join(' | ') || ''
        );
        Sentry.setTag(
          'groupPermissions',
          djangoUser.groupPerms?.map((perm) => perm?.codename).join(' | ') || ''
        );
        Sentry.setTag(
          'groups',
          djangoUser.groups?.map((group) => group?.name).join(' | ') || ''
        );
        Sentry.setTag('oneviewId', djangoUser.id || '');
        Sentry.setTag('curoId', djangoUser.curoId || '');
      }
    } catch (err) {
      // do nothing
    }
  }, [djangoUser?.email]);

  if (!user || !djangoUser) {
    return <Loading />;
  }

  if (!isAzureAD && mfa === 'NOMFA') {
    const email = (user.attributes as any)?.email;
    if (!MFA_EXEMPT_EMAILS.includes(email)) {
      return <Navigate to="/2fa-enrolment" />;
    }
  }

  return <DjangoContext.Provider value={value!}>{children}</DjangoContext.Provider>;
}

export default EnforceMfa;
